import getProductAuthUrl from "../../helpers/getProductAuthUrl";
import { authService } from "../../services/auth.service";
import { handleSessionError } from "../../services/handleSessionError";
import {
  mixpanelService,
  MIXPANEL_EVENT_POTWIERDZONO_KONTO,
  MIXPANEL_EVENT_USUNIETO_KONTO,
  MIXPANEL_EVENT_ZALOGOWANO,
  MIXPANEL_EVENT_ZALOZONO_KONTO,
} from "../../services/mixpanel.service";
import { authConsts, DaneOsobiste, DaneSesji, TokenInfo } from "../types/auth";
import { Produkt, Zaproszenie } from "../types/organizacje";
import { organizacjeActions } from "./organizacje.actions";
import { uxActions } from "./ux.actions";

export const authActions = {
  login,
  loginToProduct,
  loginInvite,
  loginInviteToProduct,
  signup,
  signupToProduct,
  signupInvite,
  signupInviteToProduct,
  logout,
  confirmEmail,
  clearError,
  getPersonalData,
  changePersonalData,
  resendConfirmationEmail,
  getSessionData,
  changePassword,
  clearAccountActionsState,
  resetPasswordSendEmail,
  resetPassword,
  deleteAccount,
};

function getProductName(p: Produkt) {
  if (p === Produkt.Zioom) return "Faktury.raks.pl";
  else if (p === Produkt.Brunet) return "Brunet";
  else return "";
}

function login(email: string, pass: string): Function {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa pobieranie danych.."));
    dispatch(request(email));

    return authService
      .login(email, pass)
      .then(
        (user) => {
          mixpanelService.login(email);
          mixpanelService.uaktualnijProfilUzytkownika(email, {
            $email: email,
          });
          mixpanelService.rejestrujZdarzenie(MIXPANEL_EVENT_ZALOGOWANO, {
            Aplikacja: "ID_RAKS",
            Zaproszenie: false,
          });
          dispatch(success(user.UserName, user.UserId));
          dispatch(getSessionData());
        },
        (error) => {
          dispatch(setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request(user: string) {
    return { type: authConsts.LOGIN_REQUEST, user };
  }
  function success(user: string, userId: string) {
    return { type: authConsts.LOGIN_SUCCESS, user, userId };
  }
  function setError(error: string) {
    return { type: authConsts.LOGIN_FAILURE, error: error };
  }
}

function loginToProduct(
  email: string,
  pass: string,
  product: Produkt,
  host: string,
  redirect: string
): Function {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa pobieranie danych.."));
    dispatch(request(email));

    return authService
      .getLoginToken(email, pass, product)
      .then(
        (res: { Token: string; CzyMaDostep: boolean }) => {
          if (res.CzyMaDostep) {
            mixpanelService.login(email);
            mixpanelService.uaktualnijProfilUzytkownika(email, {
              $email: email,
            });
            mixpanelService.rejestrujZdarzenie(MIXPANEL_EVENT_ZALOGOWANO, {
              Aplikacja: getProductName(product).toUpperCase(),
              Zaproszenie: false,
            });
            window.location.href = getProductAuthUrl(
              decodeURIComponent(host),
              email,
              res.Token,
              decodeURIComponent(redirect)
            );
          } else {
            authService.login(email, pass).then(
              (user) => {
                dispatch(success(user.UserName, user.UserId));
                dispatch(getSessionData());
                dispatch(
                  uxActions.showToaster(
                    `Nie masz uprawnień do pracy w aplikacji ${getProductName(
                      product
                    )}.`,
                    "warning"
                  )
                );
              },
              (error) => {
                dispatch(setError(error));
              }
            );
          }
        },
        (error) => {
          dispatch(setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request(user: string) {
    return { type: authConsts.LOGIN_REQUEST, user };
  }
  function success(user: string, userId: string) {
    return { type: authConsts.LOGIN_SUCCESS, user, userId };
  }
  function setError(error: string) {
    return { type: authConsts.LOGIN_FAILURE, error: error };
  }
}

function loginInvite(
  email: string,
  pass: string,
  idOrganizacji: string,
  idZaproszenia: string
) {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa zapis danych.."));
    dispatch(request(email));

    authService
      .login(email, pass)
      .then(
        (user) => {
          dispatch(
            organizacjeActions.akceptujZaproszenie(idOrganizacji, idZaproszenia)
          ).then((result: Zaproszenie) => {
            if (result) {
              mixpanelService.login(email);
              mixpanelService.uaktualnijProfilUzytkownika(email, {
                $email: email,
              });
              mixpanelService.rejestrujZdarzenie(MIXPANEL_EVENT_ZALOGOWANO, {
                Aplikacja: "ID_RAKS",
                Zaproszenie: true,
              });
              dispatch(success(user.UserName, user.UserId));
              dispatch(getSessionData());
            }
          });
        },
        (error) => dispatch(setError(error))
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request(user: string) {
    return { type: authConsts.LOGIN_REQUEST, user };
  }
  function success(user: string, userId: string) {
    return { type: authConsts.LOGIN_SUCCESS, user, userId };
  }
  function setError(error: string) {
    return { type: authConsts.LOGIN_FAILURE, error: error };
  }
}

function loginInviteToProduct(
  email: string,
  pass: string,
  idOrganizacji: string,
  idZaproszenia: string,
  host: string,
  redirect: string
): Function {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa pobieranie danych.."));
    dispatch(request(email));

    authService
      .login(email, pass)
      .then(
        () => {
          dispatch(
            organizacjeActions.akceptujZaproszenie(idOrganizacji, idZaproszenia)
          ).then((result: Zaproszenie) => {
            if (result)
              // biorę pierwszy produkt ponieważ zaproszenie wysłane z produktu, zaprasza tylko do tego, jednego produktu
              authService.getLoginToken(email, pass, result.Produkty[0]).then(
                (dane: TokenInfo) => {
                  mixpanelService.login(email);
                  mixpanelService.uaktualnijProfilUzytkownika(email, {
                    $email: email,
                  });
                  mixpanelService.rejestrujZdarzenie(
                    MIXPANEL_EVENT_ZALOGOWANO,
                    {
                      Aplikacja: getProductName(
                        result.Produkty[0]
                      ).toUpperCase(),
                      Zaproszenie: true,
                    }
                  );
                  window.location.href = getProductAuthUrl(
                    decodeURIComponent(host),
                    email,
                    dane.Token,
                    decodeURIComponent(redirect)
                  );
                },
                (error) => dispatch(setError(error))
              );
          });
        },
        (error) => dispatch(setError(error))
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request(user: string) {
    return { type: authConsts.LOGIN_REQUEST, user };
  }
  function setError(error: string) {
    return { type: authConsts.LOGIN_FAILURE, error: error };
  }
}

function signup(email: string, pass: string): Function {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa zapis danych.."));
    dispatch(request(email));

    return authService
      .signup(email, pass)
      .then(
        () => {
          return authService.login(email, pass).then(
            (user) => {
              mixpanelService.uaktualnijProfilUzytkownika(email, {
                $email: email,
              });
              mixpanelService.rejestrujZdarzenie(
                MIXPANEL_EVENT_ZALOZONO_KONTO,
                { Aplikacja: "ID_RAKS", Zaproszenie: false }
              );
              dispatch(success(user.UserName, user.UserId));
              dispatch(getSessionData());
              dispatch(
                uxActions.showToaster("Wysłano e-mail aktywacyjny!", "success")
              );
            },
            (error) => {
              dispatch(setError(error));
            }
          );
        },
        (error) => {
          dispatch(setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request(user: string) {
    return { type: authConsts.SIGNUP_REQUEST, user };
  }
  function success(user: string, userId: string) {
    return { type: authConsts.SIGNUP_SUCCESS, user, userId };
  }
  function setError(error: string) {
    return { type: authConsts.SIGNUP_FAILURE, error: error };
  }
}

function signupToProduct(
  email: string,
  pass: string,
  product: Produkt,
  host: string,
  redirect: string
) {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa zapis danych.."));
    dispatch(request(email));

    return authService
      .signupToProduct(email, pass, product)
      .then(
        () => {
          authService.getLoginToken(email, pass, product).then(
            (res: { Token: string }) => {
              mixpanelService.uaktualnijProfilUzytkownika(email, {
                $email: email,
              });
              mixpanelService.rejestrujZdarzenie(
                MIXPANEL_EVENT_ZALOZONO_KONTO,
                {
                  Aplikacja: getProductName(product).toUpperCase(),
                  Zaproszenie: false,
                }
              );
              window.location.href = getProductAuthUrl(
                decodeURIComponent(host),
                email,
                res.Token,
                getSignupRedirect(decodeURIComponent(redirect))
              );
            },
            (error) => {
              dispatch(setError(error));
            }
          );
        },
        (error) => {
          dispatch(setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function getSignupRedirect(path: string) {
    if (path === "/") return "/signedin";
    return path + "/signedin";
  }

  function request(user: string) {
    return { type: authConsts.SIGNUP_REQUEST, user };
  }
  function setError(error: string) {
    return { type: authConsts.SIGNUP_FAILURE, error: error };
  }
}

function signupInvite(
  email: string,
  pass: string,
  idOrganizacji: string,
  idZaproszenia: string
) {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa zapis danych.."));
    dispatch(request(email));

    return authService
      .signup(email, pass)
      .then(
        () => {
          authService.login(email, pass).then(
            (user) => {
              dispatch(
                organizacjeActions.akceptujZaproszenie(
                  idOrganizacji,
                  idZaproszenia
                )
              ).then((result: Zaproszenie) => {
                if (result) {
                  mixpanelService.uaktualnijProfilUzytkownika(email, {
                    $email: email,
                  });
                  mixpanelService.rejestrujZdarzenie(
                    MIXPANEL_EVENT_ZALOZONO_KONTO,
                    { Aplikacja: "ID_RAKS", Zaproszenie: true }
                  );
                  dispatch(success(user.UserName, user.UserId));
                  dispatch(getSessionData());
                }
              });
            },
            (error) => dispatch(setError(error))
          );
        },
        (error) => dispatch(setError(error))
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request(user: string) {
    return { type: authConsts.SIGNUP_REQUEST, user };
  }
  function success(user: string, userId: string) {
    return { type: authConsts.SIGNUP_SUCCESS, user, userId };
  }
  function setError(error: string) {
    return { type: authConsts.SIGNUP_FAILURE, error: error };
  }
}

function signupInviteToProduct(
  email: string,
  pass: string,
  idOrganizacji: string,
  idZaproszenia: string,
  host: string,
  redirect: string
) {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa zapis danych.."));
    dispatch(request(email));

    return authService
      .signup(email, pass)
      .then(
        () => {
          authService.login(email, pass).then(
            () => {
              dispatch(
                organizacjeActions.akceptujZaproszenie(
                  idOrganizacji,
                  idZaproszenia
                )
              ).then((result: Zaproszenie) => {
                if (result)
                  // biorę pierwszy produkt ponieważ zaproszenie wysłane z produktu, zaprasza tylko do tego, jednego produktu
                  authService
                    .getLoginToken(email, pass, result.Produkty[0])
                    .then(
                      (dane: TokenInfo) => {
                        mixpanelService.uaktualnijProfilUzytkownika(email, {
                          $email: email,
                        });
                        mixpanelService.rejestrujZdarzenie(
                          MIXPANEL_EVENT_ZALOZONO_KONTO,
                          {
                            Aplikacja: getProductName(
                              result.Produkty[0]
                            ).toUpperCase(),
                            Zaproszenie: true,
                          }
                        );
                        window.location.href = getProductAuthUrl(
                          decodeURIComponent(host),
                          email,
                          dane.Token,
                          decodeURIComponent(redirect)
                        );
                      },
                      (error) => dispatch(setError(error))
                    );
              });
            },
            (error) => dispatch(setError(error))
          );
        },
        (error) => dispatch(setError(error))
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request(user: string) {
    return { type: authConsts.SIGNUP_REQUEST, user };
  }
  function setError(error: string) {
    return { type: authConsts.SIGNUP_FAILURE, error: error };
  }
}

function logout(): Function {
  return (dispatch: Function) => {
    authService.logout();
    dispatch({ type: authConsts.LOGOUT });
  };
}

function confirmEmail(email: string, token: string): Function {
  return (dispatch: Function) => {
    dispatch(request(email));
    return authService.confirmEmail(email, token).then(
      () => {
        mixpanelService.login(email);
        mixpanelService.uaktualnijProfilUzytkownika(email, {
          $email: email,
        });
        mixpanelService.rejestrujZdarzenie(
          MIXPANEL_EVENT_POTWIERDZONO_KONTO,
          {}
        );
        dispatch(success());
        return true;
      },
      (error) => {
        dispatch(setError(error));
      }
    );
  };

  function request(user: string) {
    return { type: authConsts.CONFIRM_EMAIL_REQUEST, user };
  }
  function success() {
    return { type: authConsts.CONFIRM_EMAIL_SUCCESS };
  }
  function setError(error: string) {
    return { type: authConsts.CONFIRM_EMAIL_FAILURE, error: error };
  }
}

function clearError() {
  return { type: authConsts.CLEAR_ERROR };
}

function getPersonalData() {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa pobieranie danych.."));
    dispatch(request());

    return authService
      .getPersonalData()
      .then(
        (dto: DaneOsobiste) => {
          dispatch(success());
          return dto;
        },
        (error) => {
          handleSessionError(dispatch, error, setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request() {
    return { type: authConsts.GET_PERSONAL_DATA_REQUEST };
  }
  function success() {
    return { type: authConsts.GET_PERSONAL_DATA_SUCCESS };
  }
  function setError(error: string) {
    return { type: authConsts.GET_PERSONAL_DATA_FAILURE, error: error };
  }
}

function changePersonalData(imie: string, nazwisko: string) {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa zapis danych.."));
    dispatch(request());

    return authService
      .changePersonalData(imie, nazwisko)
      .then(
        () => {
          dispatch(success());
          dispatch(uxActions.showToaster("Zapisano!", "success"));
        },
        (error) => {
          handleSessionError(dispatch, error, setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request() {
    return { type: authConsts.CHANGE_PERSONAL_DATA_REQUEST };
  }
  function success() {
    return { type: authConsts.CHANGE_PERSONAL_DATA_SUCCESS };
  }
  function setError(error: string) {
    return { type: authConsts.CHANGE_PERSONAL_DATA_FAILURE, error: error };
  }
}

function resendConfirmationEmail() {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Wysyłanie wiadomości.."));
    dispatch(request());

    return authService
      .resendConfirmationEmail()
      .then(
        () => {
          dispatch(success());
          dispatch(uxActions.showToaster("Wysłano!", "success"));
        },
        (error) => {
          handleSessionError(dispatch, error, setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request() {
    return { type: authConsts.RESEND_CONFIRMATION_EMAIL_REQUEST };
  }
  function success() {
    return { type: authConsts.RESEND_CONFIRMATION_EMAIL_SUCCESS };
  }
  function setError(error: string) {
    return { type: authConsts.RESEND_CONFIRMATION_EMAIL_FAILURE, error: error };
  }
}

function getSessionData(): Function {
  return (dispatch: Function) => {
    dispatch(request());

    return authService.dajDaneSesji().then(
      (data) => {
        dispatch(success(data));
      },
      (error) => {
        handleSessionError(dispatch, error, setError(error));
      }
    );
  };

  function request() {
    return { type: authConsts.GET_SESSION_DATA_REQUEST };
  }
  function success(data: DaneSesji) {
    return { type: authConsts.GET_SESSION_DATA_SUCCESS, sesja: data };
  }
  function setError(error: string) {
    return { type: authConsts.GET_SESSION_DATA_FAILURE, error: error };
  }
}

function changePassword(haslo: string, noweHaslo: string) {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa zapis danych.."));
    dispatch(request());

    return authService
      .changePassword(haslo, noweHaslo)
      .then(
        () => {
          dispatch(success());
          dispatch(uxActions.showToaster("Zapisano!", "success"));
          return true;
        },
        (error) => {
          handleSessionError(dispatch, error, setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request() {
    return { type: authConsts.CHANGE_PSWD_REQUEST };
  }
  function success() {
    return { type: authConsts.CHANGE_PSWD_SUCCESS };
  }
  function setError(error: string) {
    return { type: authConsts.CHANGE_PSWD_FAILURE, error: error };
  }
}

function clearAccountActionsState() {
  return { type: authConsts.CLEAR_ACCOUNT_ACTN_STATE };
}

function resetPasswordSendEmail(email: string): Function {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Wysyłanie wiadomości.."));
    dispatch(request());

    authService
      .resetPasswordSendEmail(email)
      .then(
        (dto) => {
          dispatch(success());
          return dto;
        },
        (error) => {
          dispatch(setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request() {
    return { type: authConsts.RESET_PSWD_SEND_EMAIL_REQUEST };
  }
  function success() {
    return { type: authConsts.RESET_PSWD_SEND_EMAIL_SUCCESS };
  }
  function setError(error: string) {
    return { type: authConsts.RESET_PSWD_SEND_EMAIL_FAILURE, error: error };
  }
}

function resetPassword(email: string, token: string, pass: string): Function {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa zapis danych.."));
    dispatch(request(email));

    authService
      .resetPassword(email, token, pass)
      .then(
        (dto) => {
          dispatch(success());
          return dto;
        },
        (error) => {
          dispatch(setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request(user: string) {
    return { type: authConsts.RESET_PSWD_REQUEST, user };
  }
  function success() {
    return { type: authConsts.RESET_PSWD_SUCCESS };
  }
  function setError(error: string) {
    return { type: authConsts.RESET_PSWD_FAILURE, error: error };
  }
}

function deleteAccount(password: string) {
  return (dispatch: Function) => {
    dispatch(uxActions.showProgress("Trwa usuwanie danych.."));
    dispatch(request());

    return authService
      .deleteAccount(password)
      .then(
        () => {
          mixpanelService.rejestrujZdarzenie(MIXPANEL_EVENT_USUNIETO_KONTO, {});
          dispatch(success);
          return true;
        },
        (error) => {
          handleSessionError(dispatch, error, setError(error));
        }
      )
      .finally(() => dispatch(uxActions.hideProgress()));
  };

  function request() {
    return { type: authConsts.DELETE_ACCOUNT_REQUEST };
  }
  function success() {
    return { type: authConsts.DELETE_ACCOUNT_SUCCESS };
  }
  function setError(error: string) {
    return { type: authConsts.DELETE_ACCOUNT_FAILURE, error: error };
  }
}
